import { ChangeDetectorRef, Directive, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewRef, inject } from '@angular/core';
import { Basket as BasketNamespace } from '@df/basket/basket.interfaces';
import { IProductDataV3, Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { NgInject } from '@df/ng/ng-inject';
import { TimeoutService } from '@df/ng/timeout.service';
import { BasketItem, IBasketItemConfigurableOption } from '@df/session/api/basket/basket-item';
import { IResponseRemoveFromBasket } from '@df/session/api/basket/basket.service';
import { Customer } from '@df/session/api/customer';
import isNumber from 'lodash-es/isNumber';
import { Subscription } from 'rxjs';

@Directive()
export abstract class BasketItemComponentDef<T extends BasketItem = BasketItem> implements OnDestroy, OnInit, OnChanges {
    protected subscriptions: Subscription = new Subscription();

    saveQty: (qty: number) => void;

    /**
     * If change detection can be async
     */
    protected canDetach = true;

    /**
     *
     */
    protected initiated = false;

    /**
     * Basket item
     */
    private _item?: T;

    /**
     * Basket item setter
     */
    @Input({
        required: true
    })
    set item(item: T) {
        if (this._item !== item) {
            this._item = item;
            this.update();
        }
    }

    /**
     * Basket item getter
     */
    get item(): T | undefined {
        return this._item;
    }

    // /**
    //  * Custom slot for any data passing
    //  * @type {any}
    //  */
    // @Input() custom: any;

    /**
     * Status model for remove action
     */
    readonly removeStatus = new Status();

    /**
     * Status object for qty changes
     * @type {Status}
     */
    readonly status = new Status();

    /**
     * Data model
     */
    data: BasketNamespace.IItemComponentData = {};

    /**
     * Last qty value saved
     */
    private _lastSavedQty?: number;

    /**
     * App service
     */
    readonly app = inject(App);

    private timeoutService = inject(TimeoutService);

    /**
     * Basket model
     */
    get basket() {
        return this.customer.basket;
    }

    /**
     * Item product
     */
    product?: Product<IProductDataV3>; // may not be set when product is not indexed or something

    rowPrice?: number;
    maxStandaloneQty?: number;
    maxQty?: number;
    maxInQty?: number;
    isSalable?: boolean;
    rowOrgPrice: number | undefined;
    rowDiscount: number | undefined;
    basketSubscription?: Subscription;

    constructor(
        protected _ref: ChangeDetectorRef,
        readonly customer: Customer
    ) {
        this.timeoutService.setTimeout(() => {
            if (this.canDetach) {
                this._ref.detach();
            }
        });
        this.saveQty = this.timeoutService.debounce((qty: number = this.getDataQty()) => this._saveQty(qty), 1000);

        this.subscriptions.add(this.status.subscribe(() => this.detectChanges()));
    }

    ngOnInit() {
        this.subscriptions.add(this.app.client.obs.breakpoint.subscribe(() => this.detectChanges()));
        this.initiated = true;
        this.detectChanges();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.detectChanges();
    }

    /**
     * On destroy hook
     */
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    update(sync: boolean = false) {
        if (this.item) {
            this.product = this.item.product;
            this.data.qty = this._lastSavedQty = this.item.qty;
            this.updateQtyDependentData();
            this.maxQty = this.item.maxQty;
            this.maxInQty = this.item.maxInQty;
            this.maxStandaloneQty = this.item.maxStandaloneQty;
        }
        this._ref.detectChanges();
    }

    get editUrl(): string | undefined {
        return this.product && this.product.data && this.product.data.url + '#item=' + this.item!.id;
    }

    /**
     * Removes item from basket
     * @return {promise}
     */
    remove(): Promise<IResponseRemoveFromBasket> {
        return this.item!.remove(this.removeStatus);
    }

    checkoutRemove(): Promise<IResponseRemoveFromBasket> {
        return this.item!.remove(this.removeStatus, undefined, undefined, true);
    }

    /**
     * Saves qty
     * @return {promise}
     */
    protected async _saveQty(qty: number = this.getDataQty()): Promise<any> {
        if (this._lastSavedQty === qty) {
            return;
        }

        this._lastSavedQty = qty;

        return this.item!.setQty(this.status, qty, undefined, error => {
            this.customer.model.load(true);
        });
    }

    /**
     * Increases qty
     * @param  {int} qty default 1
     */
    more(qty: number = 1) {
        this.setQty(this.getDataQty() + qty);
    }

    /**
     * Decreases qty
     * @param {int} qty default 1
     * @param {boolean} allowZero default false, if true will allow deleting by setting qty to 0
     */
    less(qty: number = 1, allowZero: boolean = false) {
        this.setQty(this.getDataQty() - qty, allowZero);
    }

    /**
     * Sets qty
     * @param {int} qty default 1
     * @param {boolean} allowZero default false, if true will allow deleting by setting qty to 0
     */
    setQty = (qty, allowZero = false) => {
        if (qty < 0) {
            qty = 0;
        }
        if (!qty && allowZero !== true) {
            // with qty-select we may pass object with controller as second param
            return;
        }

        if (this.maxQty && qty > this.maxQty) {
            qty = this.maxQty;
        }

        if (this.data.qty !== qty) {
            this.data.qty = qty;
            this.updateQtyDependentData();
            this.onDataQtyChange();
            this.detectChanges();
        }
    };

    protected updateQtyDependentData() {
        const qty = this.data.qty;
        if (qty) {
            this.rowPrice = this.item!.rowPrice(qty);
            this.rowOrgPrice = this.item!.rowOrgPrice(qty);
            this.rowDiscount = this.item!.rowDiscount(qty);
            this.isSalable = this.item!.isSalable(qty);
        }
    }

    /**
     * Triggered when qty chagnes
     */
    onDataQtyChange() {
        if (isNumber(this.data.qty)) {
            this.saveQty(this.data.qty);
        }
    }

    /**
     * Retrieves qty from data model
     */
    getDataQty(): number {
        return this.data.qty || 0;
    }

    trackByConfigurableCode(index: number, option: IBasketItemConfigurableOption) {
        return option.code;
    }

    protected detectChanges(): void {
        if (this.initiated) {
            if (!(<ViewRef>this._ref).destroyed) {
                this._ref.detectChanges();
            }
        }
    }
}
